<template>
    <div>
        <loader :loader="isLoading" v-if="isLoading" />
        <div v-if="!isLoading">
            <b-row class="row target-row-filter">
                <b-col>
                    <h3 class="personalization-filter">
                        <i class="fa fa-calendar" aria-hidden="true"></i>
                        {{ $t('profile.changeFilter').toUpperCase() }}
                    </h3>
                    <multiselect
                        :state="null"
                        :selected="consolidatedGroups"
                        :options="consolidatedOptions"
                        :disabled="false"
                        :loading="false"
                        @changeValue="consolidatedGroups = $event"
                    >
                        <template slot="c-option" slot-scope="{ options: { option } }">
                            <span>{{ option.label }}</span>
                        </template>
                    </multiselect>
                </b-col>
                <b-col>
                    <div class="text-right" v-if="type == 'targets'">
                        <span id="disable-new-target-action" class="d-inline-block" tabindex="0">
                            <b-button
                                id="btn-tab-target-new"
                                size="sm"
                                type="button"
                                :disabled="!hasCreatePermission()"
                                :variant="'primary'"
                                @click="onClickNewTarget"
                            >
                                <b-tooltip target="disable-new-target-action" v-if="!hasCreatePermission()">
                                    {{ viewNewTargetTooltipMessage }}
                                </b-tooltip>
                                {{ $t('profile.targetsTable.newTarget') }}
                            </b-button>
                        </span>
                    </div>
                </b-col>
            </b-row>
            <c-list
                :consolidated="consolidated"
                :targets="targets"
                :isUserAllowedToEdit="isUserAllowedToEdit"
                :currentUserHasRolePermissions="currentUserHasRolePermissions"
                :update="handleReload"
                v-if="targets && targets.length > 0"
                :type="type"
                :isProfileOwner="isProfileOwner()"
                :userId="user.id"
            />
            <div v-else>
                <b-alert class="mt-3" show variant="info">
                    {{ $t('profile.targetsTable.empty') }}
                </b-alert>
            </div>
        </div>
    </div>
</template>
<script>
import Loader from '@/views/components/loader'
import i18n from '@/i18n'
import Swal from 'sweetalert2'
import { createNewTargetOrObjective } from '@/shared/swal-component'

import { getTargets, getTargetsConsolidate } from './api'
import { getUserPermissions, userHasAuthToModifyTarget } from '@/services/user-permissions'
import { userPermissionsMixin } from '@/mixins'
import { Multiselect } from '@/views/components/selects'
import { List as CList } from './children'
import { mapGetters } from 'vuex'

export default {
    name: 'Targets',
    mixins: [userPermissionsMixin],
    props: {
        user: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            required: false,
            default: 'targets',
        },
    },
    components: {
        Loader,
        Multiselect,
        CList,
    },
    data() {
        return {
            isLoading: true,
            targets: [],
            consolidated: {},
            permissions: {
                currentEventPermissions: {},
                isUserAllowedToEdit: false,
            },
            consolidatedGroups: [],
            consolidatedOptions: [
                {
                    value: 'unit',
                    label: i18n.t(`groupLevel.unit`),
                },
                {
                    value: 'department',
                    label: i18n.t('groupLevel.department'),
                },
                {
                    value: 'personal',
                    label: i18n.t('groupLevel.personal'),
                },
            ],
        }
    },
    mounted() {
        this.$store.watch(
            (state) => state.filters.reloadTargets,
            (reloadTargets) => {
                if (reloadTargets) {
                    this.handleReload()
                }
            }
        )
        this.getPermissions()
        this.handleReload()
    },
    methods: {
        async handleReload() {
            this.isLoading = true
            await this.getTargets()
            await this.getTargetsConsolidateFromAPI()
            this.isLoading = false
            this.updateStagePermissions
        },
        getTargets() {
            const type = ['focal-points', 'library-goals']
            if (!type.includes(this.type)) {
                return this.getTargetsFromApi('targets')
            }
            return this.getTargetsFromApi(this.type)
        },
        getTargetsFromApi(route) {
            const groups = this.mapGroupsToRequest(this.consolidatedGroups)
            this.targets = null
            return getTargets(this.user.id, this.periods, groups, route)
                .then((response) => {
                    this.targets = response
                })
                .catch((e) => {
                    console.error(e)
                })
        },
        getTargetsConsolidateFromAPI() {
            if (this.type == 'targets') {
                const groups = this.mapGroupsToRequest(this.consolidatedGroups)
                return getTargetsConsolidate(this.user.id, this.periods, groups).then((resp) => {
                    const { achieved, committeeEvaluations, managerEvaluations, ownerEvaluations, totalWeight } = resp
                    this.consolidated = {
                        achieved,
                        committeeEvaluations,
                        managerEvaluations,
                        ownerEvaluations,
                        totalWeight,
                    }
                })
            }
        },
        mapGroupsToRequest(groups) {
            return groups.map((element) => element.value)
        },
        getPermissions() {
            const { current_event_permissions } = getUserPermissions()
            this.permissions.currentEventPermissions = current_event_permissions
            this.$store.dispatch('setCurrentEventPermissions', current_event_permissions)
            this.permissions.isUserAllowedToEdit = this.isUserAllowedToEdit
        },
        showDenyAlert() {
            return Swal({
                type: 'warning',
                text: i18n.t('notification.permissions.assignDenied', {
                    item: i18n.t('targets.target'),
                }),
            })
        },
        onClickNewTarget() {
            const newTargetUrl = `/v2/target/targets/new`
            if (this.isProfileOwner()) {
                return this.hasCreatePermission(newTargetUrl)
            }
            if (
                (this.isManagerOfUser(this.currentUser, this.user) &&
                    this.permissions.currentEventPermissions.goals.consolidated.create) ||
                this.currentUserHasRolePermissions
            ) {
                return createNewTargetOrObjective({
                    title: i18n.t('profile.targetsTable.newTarget'),
                    text: i18n.t('profile.targetsTable.newTargetFor', {
                        profileOwner: this.user.name,
                    }),
                    pathIfConfirm: `${newTargetUrl}?ownerId=${this.user.id}`,
                    context: this,
                })
            }
            return this.showDenyAlert()
        },
        hasCreatePermission(newTargetUrl) {
            if (this.permissions.currentEventPermissions.goals.consolidated.create) {
                return this.$router.push(newTargetUrl)
            }
        },
        isProfileOwner() {
            return this.user.id == this.currentUser.id
        },
        updateCurrentUserId(newUserID) {
            this.user.id = newUserID
        },
    },
    watch: {
        $route(to, from) {
            if (to.params.id !== from.params.id) {
                this.updateCurrentUserId(to.params.id)
                this.handleReload()
            }
        },
        consolidatedGroups() {
            this.handleReload()
        },
        periods() {
            this.handleReload()
        },
    },
    computed: {
        ...mapGetters(['periods']),
        currentUser() {
            return this.$store.state.currentUser.user
        },
        updateStagePermissions() {
            if (!this.profileOwner && !this.currentUserHasRolePermissions) {
                const currentPermissions = this.$store.state.filters.currentEventPermissions
                currentPermissions.goals = this.$store.state.profile.user.permissions_stage
                this.$store.dispatch('setCurrentEventPermissions', currentPermissions)
                return true
            }
            return false
        },
        currentUserHasRolePermissions() {
            return this.currentUserHasRolePermissionsOf([
                'god',
                'administrator',
                'local_administrator',
                'is_target_master',
            ])
        },
        isUserAllowedToEdit() {
            return userHasAuthToModifyTarget({
                currentUserId: this.currentUser.id,
                currentUserIsAdmin: this.currentUser.role.name == 'administrator',
                currentUserIsGod: this.currentUser.role.name == 'god',
                currentUserIsLocalAdmin: this.currentUser.role.name == 'local_administrator',
                currentUserIsTargetMaster: this.currentUser.is_target_master,
                userId: this.user.id,
                userManagerId: this.user.manager.id,
            })
        },
        viewNewTargetTooltipMessage() {
            return (
                this.$t('profile.targetsTable.userWithoutPermissionNewTarget', {
                    stageTitle: this.$store.state.profile.user.stages.current.title,
                }) + this.viewStageCriteriaMessage
            )
        },
        viewStageCriteriaMessage() {
            const firstEntryCriteria = 0
            if (!this.hasEntryCriteria) {
                return ''
            }
            const criterionIdentifier =
                this.$store.state.profile.user.stages.current.entryCriteria[firstEntryCriteria].criterion.identifier
            return this.$t(`stages.eventHasCriteriaMessages.${criterionIdentifier}`)
        },
        hasEntryCriteria() {
            return Object.keys(this.$store.state.profile.user.stages.current.entryCriteria).length
        },
    },
}
</script>
<style lang="scss">
#btn-tab-target-new:disabled {
    background-color: #6e6e6e;
    border-color: #6e6e6e;
    border-radius: 5px;
    color: #ffffff;
    opacity: 0.5;
}
.personalization-filter {
    font-size: 10px;
    color: #8e8e92;
    margin-bottom: 10px;
    margin-left: 5px;
}
.target-row-filter .multiselect__tag,
.multiselect__option--highlight,
.multiselect__option--highlight:after {
    background: #57d9ff !important;
}
</style>
